import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addVendorAssessmentType,
  VendorAssessmentTypeCells,
  deleteVendorAssessmentType,
  editVendorAssessmentType,
  getVendorAssessmentTypes,
} from "framework/vendorAssessmentType";
import { Feature } from "models/api";
import { IVendorAssessmentType, IVendorAssessmentTypeInput } from "models/vendorAssessmentType";
import React, {  useState } from "react";
import { QueryClient, useMutation } from "react-query";
import AddVendorAssessmentType from "./partials/AddVendorAssessmentType/AddVendorAssessmentType";
import EditVendorAssessmentType from "./partials/EditVendorAssessmentType/EditVendorAssessmentType";
import DeleteVendorAssessmentType from "./partials/DeleteVendorAssessmentType/DeleteVendorAssessmentType";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const VendorAssessmentTypes: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterVendorAssessmentTypes, setFilterVendorAssessmentTypes] = useState<IVendorAssessmentType[]>([] as IVendorAssessmentType[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedVendorAssessmentType, setSelectedVendorAssessmentType] = useState<IVendorAssessmentType>({} as IVendorAssessmentType);
  const [inputObj, setInputObj] = useState<IVendorAssessmentTypeInput>({} as IVendorAssessmentTypeInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addVendorAssessmentTypesMutation = useMutation({
    mutationFn: (createInput: IVendorAssessmentTypeInput) => {
      return addVendorAssessmentType(createInput);
    },
  });

  const editVendorAssessmentTypesMutation = useMutation({
    mutationFn: (data: { createInput: IVendorAssessmentTypeInput; id: string }) => {
      return editVendorAssessmentType(data.createInput, data.id);
    },
  });

  const deleteVendorAssessmentTypesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteVendorAssessmentType(id);
    },
  });

  // const { loading:loadingFilters, filters, error:errorFilters, revalidate:revalidateFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     vendorassessmenttypes: getVendorAssessmentTypes,
  //   },
  //   // processFilters: {
  //   //   countries: (data) => data.filter((country) => country.active),
  //   //   vendorassessmenttypes: (data) => data.filter((vendorassessmenttype) => vendorassessmenttype.active),
  //   // },
  //   onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   },
  // });

  // const { loading:loadingFilters, filters, error:errorFilters } = useFetchMultipleFilters({
  //   fetchFilters: {
  //     countries: getCountries,
  //     vendorassessmenttypes: getVendorAssessmentTypes,
  //   },onSuccess: (data) => {
  //     console.log("Filters fetched:", data);
  //   }

  // });

  const {
    loading,
    data: VendorAssessmentTypes,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getVendorAssessmentTypes(),
    fetchPermission: () => getPermission(45),
    section: "VendorAssessmentTypes",
    onSuccess: (data) => setFilterVendorAssessmentTypes(data),
    processResponse: (data) =>
      data.map((item: IVendorAssessmentType) => {
        return { ...item, id: item.id };
      }),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any, setValue?: any) => {
    if ((name == 'isPretest' || name == 'isPosttest' || name == 'isCertifiedtest' || name == 'isPsychometrictest') && value == null) {
      value = false;
    }
    
    // For checkbox fields, enforce mutual exclusivity: only one can be selected at a time
    if (name === 'isPretest' || name === 'isPosttest' || name === 'isCertifiedtest' || name === 'isPsychometrictest') {
      // If checking a checkbox (value === true), uncheck all others
      if (value === true) {
        // Update our state
        setInputObj((prev) => ({
          ...prev,
          isPretest: name === 'isPretest' ? true : false,
          isPosttest: name === 'isPosttest' ? true : false,
          isCertifiedtest: name === 'isCertifiedtest' ? true : false,
          isPsychometrictest: name === 'isPsychometrictest' ? true : false,
        }));
        
        // Update react-hook-form state if setValue is available
        if (setValue) {
          setValue('isPretest', name === 'isPretest' ? true : false, { shouldValidate: false });
          setValue('isPosttest', name === 'isPosttest' ? true : false, { shouldValidate: false });
          setValue('isCertifiedtest', name === 'isCertifiedtest' ? true : false, { shouldValidate: false });
          setValue('isPsychometrictest', name === 'isPsychometrictest' ? true : false, { shouldValidate: false });
        }
        return;
      }
      // If unchecking (value === false), just update that specific checkbox
      // This allows users to have all checkboxes unchecked
      setInputObj((prev) => ({
        ...prev,
        [name]: false,
      }));
      
      // Update react-hook-form state if setValue is available
      if (setValue) {
        setValue(name, false, { shouldValidate: false });
      }
      return;
    }
    
    // For other fields, update normally
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () =>{setInputObj({} as IVendorAssessmentTypeInput);setOpenAdd(false);} 

  const handleOpenEdit = (VendorAssessmentType: IVendorAssessmentType) => {
    setSelectedVendorAssessmentType(VendorAssessmentType);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setInputObj({} as IVendorAssessmentTypeInput)
    setSelectedVendorAssessmentType({} as IVendorAssessmentType);
  };

  const handleOpenDelete = (VendorAssessmentType: IVendorAssessmentType) => {
    setSelectedVendorAssessmentType(VendorAssessmentType);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedVendorAssessmentType({} as IVendorAssessmentType);
  };

  const getBodyCells = (row: IVendorAssessmentType) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };


      if (key === "isPretest")
        return {
          id: "isPretest",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isPretest ? 'Yes' : "No"}</Typography>
            </TableCell>
          ),
        };


      if (key === "isPosttest")
        return {
          id: "isPosttest",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isPosttest ? 'Yes' : "No"}</Typography>
            </TableCell>
          ),
        };

      if (key === "isCertifiedtest")
        return {
          id: "isCertifiedtest",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isCertifiedtest ? 'Yes' : "No"}</Typography>
            </TableCell>
          ),
        };

      if (key === "isPsychometrictest")
        return {
          id: "isPsychometrictest",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.isPsychometrictest ? 'Yes' : "No"}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    
    try {
      setLoading2(true);

      // Ensure nameAr is included (can be empty string)
      const createInput: IVendorAssessmentTypeInput = {
        name: inputObj.name,
        nameAr: inputObj.nameAr || "",
        isPretest: inputObj.isPretest || false,
        isPosttest: inputObj.isPosttest || false,
        isCertifiedtest: inputObj.isCertifiedtest || false,
        isPsychometrictest: inputObj.isPsychometrictest || false,
      };

      await addVendorAssessmentTypesMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "VendorAssessmentType added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IVendorAssessmentTypeInput);
      setLoading2(false);
      revalidate();
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      
      let createInput: IVendorAssessmentTypeInput = {
        name: inputObj.name || selectedVendorAssessmentType.name,
        nameAr: inputObj.nameAr === null?"" :inputObj.nameAr || selectedVendorAssessmentType.nameAr,
        isPretest: inputObj.isPretest ?? (selectedVendorAssessmentType.isPretest || false),
        isPosttest: inputObj.isPosttest ?? (selectedVendorAssessmentType.isPosttest || false),
        isCertifiedtest: inputObj.isCertifiedtest ?? (selectedVendorAssessmentType.isCertifiedtest || false),
        isPsychometrictest: inputObj.isPsychometrictest ?? (selectedVendorAssessmentType.isPsychometrictest || false),
      };
      await editVendorAssessmentTypesMutation.mutateAsync({
        createInput,
        id: selectedVendorAssessmentType.id,
      });
      setAlert({
        open: true,
        message: "VendorAssessmentType updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IVendorAssessmentTypeInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteVendorAssessmentTypesMutation.mutateAsync(selectedVendorAssessmentType.id);
      setAlert({
        open: true,
        message: "VendorAssessmentType deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterVendorAssessmentTypes(VendorAssessmentTypes!);
    let filtered = VendorAssessmentTypes!.filter((vendorassessmenttype) =>
      vendorassessmenttype.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterVendorAssessmentTypes(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={t("T_search")} />
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterVendorAssessmentTypes.length > 0 && (
          <List
            headCells={VendorAssessmentTypeCells}
            data={FilterVendorAssessmentTypes}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterVendorAssessmentTypes || FilterVendorAssessmentTypes.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_assessments")}
              message={t("T_no_assessments")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddVendorAssessmentType
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditVendorAssessmentType
          open={openEdit}
          loading={loading2}
          vendorAssessmentType={selectedVendorAssessmentType}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteVendorAssessmentType
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default VendorAssessmentTypes;
